import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { ButtonWithSound } from "../AudioProvider";
import levelImg from "./assets/level_up.png";
import { useScreenType } from "../../hooks/useScreenType";
import { useUser } from "../UserProvider";
import { useEffect } from "react";
import { useRewardsCenter } from "../RewardsCenter";

import blueChips from "./assets/one_blue_chip.svg";
import redChips from "./assets/one_red_chip.svg";
import rewardPoints from "./assets/reward_points.svg";
import * as Types from "../../graphql/generated";

const BALANCE_IMAGES: { [key: string]: string } = {
  BLUE_CHIPS: blueChips,
  GOLD_CHIPS: redChips,
  REWARD_POINTS: rewardPoints,
};

const PopupContainer = ({ children }: { children?: React.ReactNode }) => {
  const screenType = useScreenType();

  return (
    <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-50">
      {screenType === "DESKTOP" ? (
        <AbsoluteCenter>{children}</AbsoluteCenter>
      ) : (
        <div className="flex justify-center items-center h-screen">
          {children}
        </div>
      )}
    </div>
  );
};

const Popup = ({
  level,
  reward,
  onClose,
  openRewardCenter,
}: {
  level: number;
  reward: { value: number; currency: Types.Currency };
  onClose: () => void;
  openRewardCenter: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <PopupContainer>
      <div
        aria-label={t("topup")}
        className="relative w-1/3 p-4 rounded shadow-xl bg-blue-800 border-blue-900 border-2"
        style={{
          minWidth: 300,
          border: "1px solid #1E71D3",
          background: "linear-gradient(180deg, #306DAE 0%, #063358 100%)",
          padding: "50px 0 40px",
          borderRadius: "20px",
        }}
      >
        <img
          src={levelImg}
          alt="cup"
          style={{
            position: "absolute",
            left: "50%",
            top: 0,
            transform: "translate(-50%, -50%)",
          }}
        />
        <div className="flex flex-col gap-6 px-8">
          <div className="flex flex-col gap-1">
            <div className="text-center text-white font-bold mb-2 text-xl leading-6">
              <Trans
                i18nKey="xp_system.level_up"
                values={{ level }}
                components={{
                  level: <span style={{ color: "#FFDA33" }} />,
                }}
              />
            </div>
            <div className="flex flex-col justify-center items-center gap-1">
              <p
                style={{
                  fontSize: "12px",
                  color: "#ffffff",
                  opacity: 0.6,
                }}
              >
                {t("xp_system.your_rewards")}
              </p>
              <div
                className="flex items-center px-6 py-1 rounded-full gap-1 text-white"
                style={{
                  background:
                    "linear-gradient(90deg, #0D3867 0%, #093360 100%)",
                  border: "0.5px solid #4B0395",
                  boxShadow: "0px 1.5px 0px 0px #859A66 inset",
                }}
              >
                <img
                  src={BALANCE_IMAGES[reward?.currency]}
                  alt=""
                  width={16}
                  height={16}
                />
                {reward?.value}
              </div>
            </div>
          </div>
          <div className="flex -mx-2">
            <div className="flex-1 px-2">
              <ButtonWithSound
                onClick={onClose}
                className="btn btn-green-600 btn-sm w-full"
                style={{
                  padding: "0 24px",
                  borderRadius: "8px",
                  lineHeight: "140%",
                  height: "34px",
                  fontWeight: 500,
                }}
              >
                {t("xp_system.keep_playing")}
              </ButtonWithSound>
            </div>
            <div className="flex-1 px-2">
              <ButtonWithSound
                className="btn btn-gray-500 btn-sm w-full"
                onClick={openRewardCenter}
                style={{
                  padding: "0 24px",
                  borderRadius: "8px",
                  lineHeight: "140%",
                  height: "34px",
                  fontWeight: 500,
                }}
              >
                {t("xp_system.rewards_center")}
              </ButtonWithSound>
            </div>
          </div>
        </div>
      </div>
    </PopupContainer>
  );
};

export const ChangeXPLevel = () => {
  const [showModal, setShowModal] = React.useState(false);
  const { userLevel, user } = useUser();
  const { openRewardCenter } = useRewardsCenter();

  const handleOpenRewardCenter = () => {
    setShowModal(false);
    openRewardCenter();
  };

  useEffect(() => {
    if (!!userLevel && userLevel !== user?.userLevelInfo?.level) {
      setShowModal(true);
    }
  }, [userLevel, user?.userLevelInfo?.level]);

  if (!showModal) {
    return <></>;
  }

  return (
    <Popup
      level={user?.userLevelInfo?.level}
      reward={user?.userLevelInfo?.reward}
      onClose={() => setShowModal(false)}
      openRewardCenter={handleOpenRewardCenter}
    />
  );
};
